package com.bikompanion.data

import dev.zacsweers.metro.Inject
import io.ktor.client.plugins.ClientRequestException
import io.ktor.http.HttpStatusCode
import com.bikompanion.domain.BikeNetwork
import com.bikompanion.domain.BikeNetworkRepository
import com.bikompanion.domain.BikeStation
import com.bikompanion.domain.RateLimitedException
import com.bikompanion.domain.Snapshot
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

/**
 * The worldwide CityBikes source — see [BikeDataSource]. Nothing binds it directly; the
 * binding lives in [NetworkProviders] alongside the switch.
 *
 * CityBikes scrapes AMBici (Barcelona's other bike-share operator) as sixteen separate
 * per-municipality networks — `ambici-badalona`, `ambici-gava`, and so on, all named "Ambici" —
 * rather than as the one physical fleet it actually is. This repository used to fold those
 * sixteen into one synthetic `"ambici"` network so a rider wasn't shown sixteen identical
 * choices, but that meant a stations refresh had no way to know which municipality was actually
 * on screen and had to fetch all sixteen every time. Publishing each municipality as its own
 * real network with its own real coordinates instead lets the existing per-network viewport
 * filter (`BikeNetwork.serves`, in `shared/ui`) fetch only the ones actually in view. AMBici's
 * own numbering is scheme-wide, so without collapsing somewhere, the same bike rated from two
 * different towns would be recorded under two different [com.bikompanion.domain.BikeKey]s —
 * but that collapsing already happens independently of this repository, in
 * [com.bikompanion.domain.ratingScopeIdOf], which matches on the "ambici" substring already
 * present in every raw municipality's id and name.
 */
@OptIn(ExperimentalTime::class)
@Inject
class CityBikesRepository(
    private val api: CityBikesApi,
) : BikeNetworkRepository {
    override suspend fun fetchNetworks(): List<BikeNetwork> = translatingRateLimit { api.getNetworks() }.networks.map { it.toBikeNetwork() }

    override suspend fun fetchStations(networkId: String): Snapshot<List<BikeStation>> {
        val stations = fetchStationsFor(networkId)
        // CityBikes reports a per-station `timestamp` of its own last scrape, but it is the
        // scrape we just triggered a read of, so the read is what dates the data here.
        return Snapshot(
            value = stations,
            updatedAtEpochMillis = Clock.System.now().toEpochMilliseconds(),
            isStale = false,
        )
    }

    private suspend fun fetchStationsFor(networkId: String): List<BikeStation> =
        translatingRateLimit { api.getNetworkDetail(networkId) }.network.stations.map { station ->
            BikeStation(
                id = station.id,
                name = station.name,
                networkId = networkId,
                latitude = station.latitude,
                longitude = station.longitude,
                freeBikes = station.freeBikes,
                emptySlots = station.emptySlots ?: 0,
                electricBikes = station.extra?.ebikes ?: 0,
                supportsElectricBikes = station.extra?.ebikes != null,
            )
        }
}

private fun CityBikesNetworkDto.toBikeNetwork() =
    BikeNetwork(
        id = id,
        name = name,
        latitude = location.latitude,
        longitude = location.longitude,
        city = location.city,
        country = location.country,
        company = company.orEmpty(),
        href = href.orEmpty(),
        licenseName = license?.name.orEmpty(),
        licenseUrl = license?.url.orEmpty(),
    )

/**
 * Turns a 429 from CityBikes into a [RateLimitedException] the UI can recognise and word
 * differently from a plain connection failure — everything else is rethrown as-is.
 */
private suspend fun <T> translatingRateLimit(block: suspend () -> T): T =
    try {
        block()
    } catch (e: ClientRequestException) {
        if (e.response.status == HttpStatusCode.TooManyRequests) throw RateLimitedException(e.message) else throw e
    }
